import { Box, MenuItem, Select, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { subDays } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useDataProvider, useGetIdentity, useTranslate } from 'react-admin';

import { GetDistributorDashboardOutstandingTimelineEndpoint } from '@Common/types/bo_external/distributor/dashboard';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { getPctChange } from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

export const OutstandingTimeline: FC<{ source: 'standard' | 'er' }> = ({ source }) => {
  const testId = 'BoExternalOutstandingTimeline';
  const [days, setDays] = useState<number>(180);
  const [dataset, setDataset] = useState<{ date: Date; value: number }[] | null>(null);
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { data: identity } = useGetIdentity();

  useEffect(() => {
    (async () => {
      const {
        data,
      }: { data: GetDistributorDashboardOutstandingTimelineEndpoint['Client']['ResponseBody'] } =
        await dataProvider.getOneWithQueryParams(
          source === 'standard'
            ? 'distributor/dashboard/outstanding_timeline'
            : 'distributor/dashboard/outstanding_timeline/er',
          {
            id: identity?.id,
          },
          new URLSearchParams({
            from: subDays(new Date(), days).toISOString(),
            to: new Date().toISOString(),
          }),
        );
      setDataset(
        data.data.map((entry) => ({
          value: entry.value,
          date: new Date(entry.date),
        })),
      );
    })();
  }, [days, dataProvider, identity, source]);

  if (!dataset) {
    return;
  }

  const pctChange = getPctChange(dataset);

  let arrow: string | undefined;
  let color: string | undefined;
  if (pctChange > 0) {
    arrow = '↑';
    color = 'green';
  } else if (pctChange < 0) {
    arrow = '↓';
    color = 'red';
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box
          sx={{
            color: 'black',
            textTransform: 'none',
            margin: '10px',
          }}>
          <TypographyTranslated
            sx={{ fontWeight: '600' }}
            label="resources.distributor.dashboard.outstanding_timeline.title"
          />
          <Typography sx={{ fontWeight: 'bold' }} variant="h4">
            {formatCentsNumber(dataset[dataset.length - 1]?.value ?? 0)}
          </Typography>
          <Typography sx={{ color, fontWeight: 'bold' }}>
            {pctChange}% {arrow}
          </Typography>
        </Box>
        <Box sx={{ marginLeft: 'auto' }}>
          <Select
            data-testid={`${testId}_DaysSelect`}
            value={days}
            onChange={(event) => setDays(event.target.value as number)}
            size="small"
            sx={{ backgroundColor: '#E8E8E8', marginRight: '20px', marginTop: '20px' }}>
            <MenuItem value={0} data-testid={`${testId}_TodayOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.today')}
            </MenuItem>
            <MenuItem value={1} data-testid={`${testId}_Last2DaysOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_n_days', {
                days: 2,
              })}
            </MenuItem>
            <MenuItem value={7} data-testid={`${testId}_LastWeekOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_week')}
            </MenuItem>
            <MenuItem value={30} data-testid={`${testId}_LastMonthOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_month')}
            </MenuItem>
            <MenuItem value={60} data-testid={`${testId}_Last60DaysOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_n_days', {
                days: 60,
              })}
            </MenuItem>
            <MenuItem value={90} data-testid={`${testId}_Last90DaysOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_n_days', {
                days: 90,
              })}
            </MenuItem>
            <MenuItem value={180} data-testid={`${testId}_Last180DaysOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_n_days', {
                days: 180,
              })}
            </MenuItem>
            <MenuItem value={365} data-testid={`${testId}_LastYearOption`}>
              {translate('resources.distributor.dashboard.outstanding_timeline.range.last_year')}
            </MenuItem>
          </Select>
        </Box>
      </Box>
      <LineChart
        key={days}
        sx={{ padding: '10px' }}
        dataset={dataset}
        xAxis={[
          {
            scaleType: 'time',
            dataKey: 'date',
            min: dataset[0]?.date,
            max: dataset[dataset.length - 1]?.date,
          },
        ]}
        series={[
          {
            curve: 'linear',
            showMark: false,
            dataKey: 'value',
            color: pctChange < 0 ? 'red' : 'green',
            valueFormatter: (value) => formatCentsNumber(value ?? 0),
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => formatCentsNumber(value ?? 0),
          },
        ]}
        rightAxis={{}}
        leftAxis={null}
        height={300}
        grid={{ vertical: true, horizontal: true }}
      />
    </Box>
  );
};
